import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import { StandingsTable } from 'src/components/Standings/StandingsTable';
import { Points, StandingsData } from 'src/models';
import logging from 'src/services/logging';
import useUsers from 'src/hooks/useUsers';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from 'src/services/firebase-service';
import { CycleContext } from 'src/context/CycleContext';

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

function createData(name: string, points: number): StandingsData {
  return {
    name,
    points,
  };
}

export const StandingsPage = () => {
  const { season } = useContext(CycleContext);
  const { fetchingUsers, users } = useUsers();
  const [fetchingPoints, setFetchingPoints] = useState<boolean>(false);
  const [generatingRows, setGeneratingRows] = useState<boolean>(false);
  const [points, setPoints] = useState<Points[]>([]);
  const [overallRows, setOverallRows] = useState<StandingsData[]>([]);
  const [tab, setTab] = useState(0);
  const [firstRoundRows, setFirstRoundRows] = useState<StandingsData[]>([]);
  const [secondRoundRows, setSecondRoundRows] = useState<StandingsData[]>([]);
  const [thirdRoundRows, setThirdRoundRows] = useState<StandingsData[]>([]);
  const [fourthRoundRows, setFourthRoundRows] = useState<StandingsData[]>([]);

  const fetchPoints = useCallback(async () => {
    try {
      logging.info('Fetching points...', 'StandingsPage');
      setFetchingPoints(true);
      const q = query(collection(db, 'points'), where('season', '==', season));
      const snapshot = await getDocs(q);
      const points: Points[] = [];
      snapshot.forEach((point) => {
        points.push(point.data() as Points);
      });
      setPoints(points);
    } catch (error) {
      logging.error(error, 'StandingsPage');
    } finally {
      setFetchingPoints(false);
    }
  }, [season]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  useEffect(() => {
    const generateRows = () => {
      try {
        if (points.length > 0 && users.length > 0) {
          logging.info('Generating rows...', 'StandingsPage');
          setGeneratingRows(true);
          users.forEach((user) => {
            const uid = user.uid;
            const userPoints = points.filter((p) => p.uid === uid);
            const userFirstRoundPoints = userPoints.filter(
              (p) => p.playoffRound === 1
            );
            const userSecondRoundPoints = userPoints.filter(
              (p) => p.playoffRound === 2
            );
            const userThirdRoundPoints = userPoints.filter(
              (p) => p.playoffRound === 3
            );
            const userFourthRoundPoints = userPoints.filter(
              (p) => p.playoffRound === 4
            );
            if (userPoints.length !== 0) {
              let totalPoints = 0;
              userPoints.forEach((x) => (totalPoints = totalPoints + x.total));
              setOverallRows((prev) => [
                ...prev,
                createData(user.fullName, totalPoints),
              ]);
            }
            if (userFirstRoundPoints.length !== 0) {
              let totalPoints = 0;
              userFirstRoundPoints.forEach(
                (x) => (totalPoints = totalPoints + x.total)
              );
              setFirstRoundRows((prev) => [
                ...prev,
                createData(user.fullName, totalPoints),
              ]);
            }
            if (userSecondRoundPoints.length !== 0) {
              let totalPoints = 0;
              userSecondRoundPoints.forEach(
                (x) => (totalPoints = totalPoints + x.total)
              );
              setSecondRoundRows((prev) => [
                ...prev,
                createData(user.fullName, totalPoints),
              ]);
            }
            if (userThirdRoundPoints.length !== 0) {
              let totalPoints = 0;
              userThirdRoundPoints.forEach(
                (x) => (totalPoints = totalPoints + x.total)
              );
              setThirdRoundRows((prev) => [
                ...prev,
                createData(user.fullName, totalPoints),
              ]);
            }
            if (userFourthRoundPoints.length !== 0) {
              let totalPoints = 0;
              userFourthRoundPoints.forEach(
                (x) => (totalPoints = totalPoints + x.total)
              );
              setFourthRoundRows((prev) => [
                ...prev,
                createData(user.fullName, totalPoints),
              ]);
            }
            setGeneratingRows(false);
          });
        }
      } catch (error) {
        logging.error(error, 'StandingsPage:generateRows');
      }
    };
    generateRows();
  }, [points, users]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  if (fetchingPoints || fetchingUsers || generatingRows) {
    return (
      <Backdrop open={fetchingPoints || fetchingUsers || generatingRows}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  }

  return (
    <Container maxWidth='sm' sx={{ mt: 3 }}>
      <Card>
        <CardHeader
          title={`STANDINGS ${season.toString().slice(4, 8)}`}
          titleTypographyProps={{ flex: '1 1 100%', variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            aria-label='standings round tabs'
            variant='scrollable'
            scrollButtons='auto'
          >
            <Tab label='Overall' {...a11yProps(0)} />
            <Tab
              label='RD 1'
              {...a11yProps(1)}
              disabled={!firstRoundRows.length}
            />
            <Tab
              label='RD 2'
              {...a11yProps(2)}
              disabled={!secondRoundRows.length}
            />
            <Tab
              label='RD 3'
              {...a11yProps(3)}
              disabled={!thirdRoundRows.length}
            />
            <Tab
              label='RD 4'
              {...a11yProps(4)}
              disabled={!fourthRoundRows.length}
            />
          </Tabs>
          <TabPanel value={tab} index={0}>
            <StandingsTable rows={overallRows} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <StandingsTable rows={firstRoundRows} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <StandingsTable rows={secondRoundRows} />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <StandingsTable rows={thirdRoundRows} />
          </TabPanel>
          <TabPanel value={tab} index={4}>
            <StandingsTable rows={fourthRoundRows} />
          </TabPanel>
        </CardContent>
      </Card>
    </Container>
  );
};
